import { api } from '../api';
import { isAdmin, navigate } from '../app';
import { esc, $, $$, setHTML, formatCurrency, formatDate, statusBadge, icons } from '../helpers';
import { toast, openModal, closeModal, setBreadcrumb, skeletonTable, makeSortable, bindSearch } from '../ui';

export async function renderPayments() {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Payments' }]);
  setHTML(content, skeletonTable(8, 5));
  try {
    const data = await api.get('/payments');
    const payments = data.payments || [];
    let rows = '';
    const pendingPayments = payments.filter((p: any) => p.status === 'pending');
    payments.forEach((p: any) => {
      const isWire = p.crypto_currency === 'wire' || (!p.tx_hash && !p.amount_crypto && !p.network);
      const methodLabel = isWire ? 'Wire / Mercury' : (p.crypto_currency || 'Stablecoins');
      const detailDisplay = isWire
        ? (p.bank_reference ? `<span class="font-mono">${esc(p.bank_reference)}</span>` : '\u2014')
        : (p.tx_hash ? `<span class="font-mono" title="${esc(p.tx_hash)}">${esc(p.tx_hash.substring(0, 12))}...</span>` : '\u2014');
      rows += `<tr data-status="${esc(p.status)}">
        <td><span class="font-mono">${esc(p.invoice_number)}</span></td>
        ${isAdmin() ? `<td>${esc(p.client_name)}</td>` : ''}
        <td><strong>${formatCurrency(p.amount_usd)}</strong></td>
        <td>${esc(methodLabel)}</td>
        <td>${detailDisplay}</td>
        <td>${statusBadge(p.status)}</td>
        <td>${formatDate(p.created_at)}</td>
        ${isAdmin() ? `<td class="row-actions" style="opacity:1">${p.status === 'pending'
          ? `<button class="btn btn-primary btn-sm" data-confirm-payment="${p.id}" style="white-space:nowrap"><i data-lucide="check"></i> Confirm</button><button class="btn btn-ghost btn-sm" data-edit-payment="${p.id}"><i data-lucide="pencil"></i></button>`
          : ''}</td>` : ''}
      </tr>`;
    });
    // For customers: show unpaid invoices CTA when no payments exist
    let unpaidBanner = '';
    if (!isAdmin() && !payments.length) {
      try {
        const invData = await api.get('/invoices');
        const unpaid = (invData.invoices || []).filter((i: any) => i.status === 'sent' || i.status === 'overdue');
        if (unpaid.length > 0) {
          const totalOwed = unpaid.reduce((s: number, i: any) => s + (i.total || 0), 0);
          let invRows = '';
          unpaid.forEach((u: any) => {
            const isOverdue = u.status === 'overdue' || (u.due_date && new Date(u.due_date) < new Date());
            invRows += `<tr>
              <td class="font-mono">${esc(u.invoice_number)}</td>
              <td>${esc(u.project_title || '\u2014')}</td>
              <td><strong>${formatCurrency(u.total, u.currency)}</strong></td>
              <td>${isOverdue ? statusBadge('overdue') : statusBadge(u.status)}</td>
              <td>${formatDate(u.due_date)}</td>
              <td><a href="/invoices/${u.id}" class="btn btn-primary btn-sm" style="white-space:nowrap"><i data-lucide="wallet"></i> Pay Now</a></td>
            </tr>`;
          });
          unpaidBanner = `<div class="card mb-2" style="border-left:3px solid var(--accent)">
            <div class="card-header" style="display:flex;align-items:center;justify-content:space-between">
              <h3><i data-lucide="file-text" style="width:18px;height:18px;vertical-align:middle;margin-right:0.3rem"></i> Invoices awaiting payment</h3>
              <span style="font-weight:700;color:var(--accent)">${formatCurrency(totalOwed)}</span>
            </div>
            <div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr>
              <th>Invoice</th><th>Project</th><th>Amount</th><th>Status</th><th>Due</th><th></th>
            </tr></thead><tbody>${invRows}</tbody></table></div></div>
          </div>`;
        }
      } catch {}
    }

    const emptyMsg = !isAdmin()
      ? unpaidBanner + '<div class="empty-state"><i data-lucide="wallet"></i><p>No payments yet. Pay an invoice to see your payment history here.</p></div>'
      : '<div class="empty-state"><i data-lucide="wallet"></i><p>No payments recorded</p></div>';
    const tableHtml = !payments.length
      ? emptyMsg
      : `<div class="table-wrapper"><table id="payments-table"><thead><tr><th>Invoice</th>${isAdmin() ? '<th>Client</th>' : ''}<th data-sort="amount">Amount</th><th>Method</th><th>Reference</th><th>Status</th><th data-sort="date">Date</th>${isAdmin() ? '<th>Actions</th>' : ''}</tr></thead><tbody>${rows}</tbody></table></div>`;

    // Admin: pending payments alert banner
    let pendingBanner = '';
    if (isAdmin() && pendingPayments.length > 0) {
      const pendingTotal = pendingPayments.reduce((s: number, p: any) => s + (p.amount_usd || 0), 0);
      let pendingRows = '';
      pendingPayments.forEach((p: any) => {
        pendingRows += `<div style="display:flex;align-items:center;justify-content:space-between;padding:0.6rem 0;${p !== pendingPayments[pendingPayments.length - 1] ? 'border-bottom:1px solid var(--border);' : ''}">
          <div>
            <span class="font-mono" style="font-weight:600">${esc(p.invoice_number)}</span>
            <span style="color:var(--text-muted);margin-left:0.5rem;font-size:0.85rem">${esc(p.client_name)}</span>
            <span style="color:var(--text-muted);margin-left:0.5rem;font-size:0.8rem">${esc(p.notes || '')}</span>
          </div>
          <div style="display:flex;align-items:center;gap:0.6rem">
            <strong>${formatCurrency(p.amount_usd)}</strong>
            <button class="btn btn-primary btn-sm" data-quick-confirm="${p.id}" style="white-space:nowrap"><i data-lucide="check-circle"></i> Confirm</button>
          </div>
        </div>`;
      });
      pendingBanner = `<div class="card mb-2" style="border-left:3px solid var(--warning)">
        <div class="card-header" style="display:flex;align-items:center;justify-content:space-between">
          <h3><i data-lucide="alert-circle" style="width:18px;height:18px;vertical-align:middle;margin-right:0.3rem;color:var(--warning)"></i> ${pendingPayments.length} payment${pendingPayments.length > 1 ? 's' : ''} awaiting confirmation</h3>
          <span style="font-weight:700;color:var(--accent)">${formatCurrency(pendingTotal)}</span>
        </div>
        <div class="card-body" style="padding:0 1.2rem">${pendingRows}</div>
      </div>`;
    }

    setHTML(content, `<div class="page-enter">
      <div class="page-header"><h1>Payments</h1><div class="page-header-actions">
        <div class="search-input-wrapper"><i data-lucide="search"></i><input type="text" id="payments-search" placeholder="Search..."></div>
        ${isAdmin() ? '<button class="btn btn-primary" id="add-payment-btn"><i data-lucide="plus"></i> Record Payment</button>' : ''}
      </div></div>
      ${pendingBanner}
      <div class="filters-bar">
        <button class="filter-chip active" data-filter="all">All</button>
        <button class="filter-chip" data-filter="pending">Pending</button>
        <button class="filter-chip" data-filter="confirming">Confirming</button>
        <button class="filter-chip" data-filter="confirmed">Confirmed</button>
      </div>
      <div class="card"><div class="card-body no-pad">${tableHtml}</div></div>
    </div>`);
    icons();
    const table = document.getElementById('payments-table');
    if (table) makeSortable(table);
    bindSearch('payments-search', 'tr', '#payments-table tbody');
    $$('.filter-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        $$('.filter-chip').forEach(c => c.classList.remove('active'));
        chip.classList.add('active');
        const f = chip.getAttribute('data-filter')!;
        $$('#payments-table tbody tr').forEach((row: any) => { row.style.display = (f === 'all' || row.dataset.status === f) ? '' : 'none'; });
      });
    });
    document.getElementById('add-payment-btn')?.addEventListener('click', () => openPaymentForm());
    $$('[data-edit-payment]').forEach(el => el.addEventListener('click', () => editPayment(parseInt(el.getAttribute('data-edit-payment')!))));

    // Confirm payment buttons (both in banner and table)
    const confirmHandler = async (paymentId: number) => {
      try {
        await api.put('/payments/' + paymentId, { status: 'confirmed' });
        toast('Payment confirmed. Invoice marked as paid.', 'success');
        renderPayments();
      } catch (err: any) { toast(err.message, 'error'); }
    };
    $$('[data-quick-confirm]').forEach(el => el.addEventListener('click', () => confirmHandler(parseInt(el.getAttribute('data-quick-confirm')!))));
    $$('[data-confirm-payment]').forEach(el => el.addEventListener('click', () => confirmHandler(parseInt(el.getAttribute('data-confirm-payment')!))));
  } catch (err: any) { toast(err.message, 'error'); }
}

async function openPaymentForm() {
  let clientsOpts = '', invoicesOpts = '';
  try {
    const [cd, id] = await Promise.all([api.get('/clients'), api.get('/invoices')]);
    clientsOpts = (cd.clients || []).map((c: any) => `<option value="${c.id}">${esc(c.name)}</option>`).join('');
    invoicesOpts = (id.invoices || []).filter((i: any) => i.status !== 'paid').map((i: any) => `<option value="${i.id}" data-client="${i.client_id}" data-amount="${i.total}">${esc(i.invoice_number)} - ${formatCurrency(i.total)}</option>`).join('');
  } catch {}

  openModal('Record Payment',
    `<form id="payment-form">
      <div class="form-row"><div class="form-group"><label>Client *</label><select name="client_id" id="pay-client" required>${clientsOpts}</select></div>
      <div class="form-group"><label>Invoice *</label><select name="invoice_id" id="pay-invoice" required>${invoicesOpts}</select></div></div>
      <div class="form-row"><div class="form-group"><label>Amount (USD) *</label><input type="number" step="0.01" name="amount_usd" id="pay-amount" required></div>
      <div class="form-group"><label>Amount (Stablecoins)</label><input type="number" step="0.000001" name="amount_crypto"></div></div>
      <div class="form-row"><div class="form-group"><label>Method</label><select name="crypto_currency"><option value="wire">Wire / Mercury</option><option value="card">Card / Stripe</option><option value="USDT">USDT (BitPay)</option><option value="USDC">USDC (BitPay)</option></select></div>
      <div class="form-group"><label>Reference / TX Hash</label><input type="text" name="tx_hash" placeholder="Bank ref or blockchain TX"></div></div>
      <div class="form-row"><div class="form-group"><label>Exchange Rate</label><input type="number" step="0.0001" name="exchange_rate" value="1"></div>
      <div class="form-group"><label>Status</label><select name="status"><option value="pending">Pending</option><option value="confirming">Confirming</option><option value="confirmed">Confirmed</option></select></div></div>
      <div class="form-group"><label>Notes</label><textarea name="notes" rows="2"></textarea></div>
      <div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="pay-cancel">Cancel</button>
      <button type="submit" class="btn btn-primary">Record Payment</button></div></form>`);

  // Auto-fill amount when invoice selected
  document.getElementById('pay-invoice')?.addEventListener('change', (e) => {
    const sel = e.target as HTMLSelectElement;
    const opt = sel.selectedOptions[0];
    if (opt) {
      (document.getElementById('pay-amount') as HTMLInputElement).value = opt.getAttribute('data-amount') || '';
      (document.getElementById('pay-client') as HTMLSelectElement).value = opt.getAttribute('data-client') || '';
    }
  });
  document.getElementById('pay-cancel')?.addEventListener('click', closeModal);
  document.getElementById('payment-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const body: any = Object.fromEntries(fd);
    body.invoice_id = parseInt(body.invoice_id);
    body.client_id = parseInt(body.client_id);
    body.amount_usd = parseFloat(body.amount_usd);
    body.amount_crypto = body.amount_crypto ? parseFloat(body.amount_crypto) : null;
    body.exchange_rate = parseFloat(body.exchange_rate) || 1;
    try { await api.post('/payments', body); toast('Payment recorded', 'success'); closeModal(); navigate('/payments'); }
    catch (err: any) { toast(err.message, 'error'); }
  });
}

function editPayment(id: number) {
  openModal('Update Payment',
    `<form id="payment-edit-form">
      <div class="form-row"><div class="form-group"><label>Status</label><select name="status"><option value="pending">Pending</option><option value="confirming">Confirming</option><option value="confirmed">Confirmed</option><option value="failed">Failed</option></select></div>
      <div class="form-group"><label>TX Hash</label><input type="text" name="tx_hash"></div></div>
      <div class="form-row"><div class="form-group"><label>Amount Stablecoins</label><input type="number" step="0.000001" name="amount_crypto"></div>
      <div class="form-group"><label>Exchange Rate</label><input type="number" step="0.0001" name="exchange_rate"></div></div>
      <div class="form-row"><div class="form-group"><label>Bank Withdrawal Date</label><input type="date" name="bank_withdrawal_date"></div>
      <div class="form-group"><label>Bank Amount</label><input type="number" step="0.01" name="bank_withdrawal_amount"></div></div>
      <div class="form-group"><label>Bank Reference</label><input type="text" name="bank_reference"></div>
      <div style="border-top:1px solid var(--border);margin:0.8rem 0;padding-top:0.8rem">
        <div style="font-size:0.82rem;font-weight:600;color:var(--text-secondary);margin-bottom:0.5rem">Stablecoin Conversion</div>
        <div class="form-row"><div class="form-group"><label>Net Fiat Received ($)</label><input type="number" step="0.01" name="net_fiat_received" id="net-fiat-input" placeholder="Amount deposited to bank"></div>
        <div class="form-group"><label>Platform</label><select name="conversion_platform"><option value="">-- Select --</option><option value="Coinbase">Coinbase</option><option value="Kraken">Kraken</option><option value="BitPay">BitPay</option><option value="Other">Other</option></select></div></div>
        <div id="conversion-fee-display" style="font-size:0.82rem;color:var(--text-muted);margin-top:0.3rem"></div>
      </div>
      <div class="form-group"><label>Notes</label><textarea name="notes" rows="2"></textarea></div>
      <div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="payedit-cancel">Cancel</button>
      <button type="submit" class="btn btn-primary">Update</button></div></form>`);
  document.getElementById('payedit-cancel')?.addEventListener('click', closeModal);

  // Auto-calculate conversion fee
  document.getElementById('net-fiat-input')?.addEventListener('input', () => {
    const netFiat = parseFloat((document.getElementById('net-fiat-input') as HTMLInputElement).value) || 0;
    const amountUsd = parseFloat(($('[name="amount_crypto"]') as HTMLInputElement)?.value || '0') || parseFloat(($('[name="bank_withdrawal_amount"]') as HTMLInputElement)?.value || '0') || 0;
    const display = document.getElementById('conversion-fee-display');
    if (display && netFiat > 0 && amountUsd > 0) {
      const fee = amountUsd - netFiat;
      const pct = ((fee / amountUsd) * 100).toFixed(2);
      display.textContent = fee > 0 ? 'Conversion fee: $' + fee.toFixed(2) + ' (' + pct + '%) — deductible as business expense' : '';
    }
  });

  document.getElementById('payment-edit-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const body: any = {};
    for (const [k, v] of fd) { if (v) body[k] = v; }
    if (body.amount_crypto) body.amount_crypto = parseFloat(body.amount_crypto);
    if (body.exchange_rate) body.exchange_rate = parseFloat(body.exchange_rate);
    if (body.bank_withdrawal_amount) body.bank_withdrawal_amount = parseFloat(body.bank_withdrawal_amount);
    if (body.net_fiat_received) {
      body.net_fiat_received = parseFloat(body.net_fiat_received);
      const grossAmount = body.amount_crypto || body.bank_withdrawal_amount || body.net_fiat_received;
      body.conversion_fee = grossAmount - body.net_fiat_received;
    }
    try { await api.put('/payments/' + id, body); toast('Payment updated', 'success'); closeModal(); navigate('/payments'); }
    catch (err: any) { toast(err.message, 'error'); }
  });
}
